<template>
  <article v-if="entrada" class="mt-articulo">
    <header class="mt-articulo__cabecera">
      <div class="mt-contenedor mt-contenedor--estrecho">
        <NuxtLink :to="ruta('/blog')" class="mt-articulo__volver"><v-icon size="18">mdi-arrow-left</v-icon> Blog</NuxtLink>
        <div v-if="entrada.Categorias?.length" class="mt-articulo__categorias">
          <span v-for="c in entrada.Categorias" :key="c" class="mt-etiqueta">{{ c }}</span>
        </div>
        <h1>{{ entrada.Titulo }}</h1>
        <p v-if="entrada.Subtitulo" class="mt-articulo__sub">{{ entrada.Subtitulo }}</p>
        <ul class="mt-articulo__meta">
          <li v-if="fecha"><v-icon size="16">mdi-calendar-blank-outline</v-icon>{{ fecha }}</li>
          <li v-if="entrada.Contenido"><v-icon size="16">mdi-timer-outline</v-icon>{{ minutosLectura(entrada.Contenido) }} min de lectura</li>
          <li v-if="entrada.Autor"><v-icon size="16">mdi-account-outline</v-icon>{{ entrada.Autor }}</li>
        </ul>
      </div>
    </header>

    <div class="mt-contenedor mt-contenedor--estrecho mt-articulo__cuerpo">
      <img v-if="entrada.Imagen" class="mt-articulo__imagen" :src="imagenIDtoSRC(entrada.Imagen)" alt="" />
      <div class="mt-prosa" v-thtml="entrada.Contenido"></div>

      <div v-if="entrada.MostrarCompartir !== false" class="mt-compartir">
        <span>Compartir</span>
        <a class="mt-compartir__boton" :href="`https://api.whatsapp.com/send?text=${encodeURIComponent(entrada.Titulo + ' ' + url)}`"
          target="_blank" rel="noopener" aria-label="Compartir por WhatsApp"><v-icon size="20">mdi-whatsapp</v-icon></a>
        <a class="mt-compartir__boton" :href="`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`"
          target="_blank" rel="noopener" aria-label="Compartir en LinkedIn"><v-icon size="20">mdi-linkedin</v-icon></a>
        <a class="mt-compartir__boton" :href="`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(entrada.Titulo)}`"
          target="_blank" rel="noopener" aria-label="Compartir en X"><span class="mt-compartir__x">𝕏</span></a>
        <button type="button" class="mt-compartir__boton" aria-label="Copiar enlace" @click="copiar">
          <v-icon size="20">{{ copiado ? 'mdi-check' : 'mdi-link-variant' }}</v-icon>
        </button>
      </div>
    </div>

    <section v-if="relacionadas.length" class="mt-seccion mt-seccion--suave">
      <div class="mt-contenedor">
        <h2 class="mt-seccion__titulo">También le puede interesar</h2>
        <p class="mt-seccion__sub">Más guías para preparar su trámite.</p>
        <div class="mt-entradas">
          <MinotariaBlogTarjeta v-for="e in relacionadas" :key="e.id" :entrada="e" />
        </div>
      </div>
    </section>

    <MinotariaBlogCierre />
  </article>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useUtils } from '#imports'
import { useStore } from '../../store/index'
import { useMinotariaBlog, fechaEntrada, minutosLectura } from '../../composables/useMinotariaBlog'
import MinotariaBlogTarjeta from './MinotariaBlogTarjeta.vue'
import MinotariaBlogCierre from './MinotariaBlogCierre.vue'

const store = useStore()
const { imagenIDtoSRC } = useUtils()
// El layout monta un componente por artículo (key = slug), así que el slug no cambia durante su vida
const route = useRouter().currentRoute
const ruta = (p: string) => (route.value.params.lang ? `/${route.value.params.lang}` : '') + p
const slug = String(route.value.params.id || '')

const { entradas } = useMinotariaBlog()
const entrada = computed(() => entradas.value.find((e) => e.Slug === slug))
const fecha = computed(() => fechaEntrada(entrada.value?.Fecha))

// Primero las que comparten categoría; después, las más recientes
const relacionadas = computed(() => {
  const actual = entrada.value
  if (!actual) return []
  const otras = entradas.value.filter((e) => e.Slug !== slug)
  const comunes = (e: typeof actual) => (e.Categorias || []).filter((c) => actual.Categorias?.includes(c)).length
  return [...otras].sort((a, b) => comunes(b) - comunes(a)).slice(0, 3)
})

if (!entrada.value) {
  showError({ statusCode: 404, statusMessage: 'Page Not Found' })
} else if (store.NOTARIA) {
  // Sin useMetaTags, que añade los datos estructurados de una notaría (como en la portada)
  useSeoMeta({
    title: entrada.value.Titulo,
    description: entrada.value.Extracto || '',
    ogTitle: `${entrada.value.Titulo} | ${store.NOTARIA.Nombre}`,
    ogDescription: entrada.value.Extracto || '',
    ogType: 'article',
    ...(entrada.value.Imagen ? { ogImage: imagenIDtoSRC(entrada.value.Imagen) } : {}),
    robots: 'index, follow',
  })
  useHead({ link: [{ rel: 'canonical', href: `https://${store.host}/blog/${slug}` }] })
}

const url = ref('')
onMounted(() => { url.value = window.location.href })
const copiado = ref(false)
async function copiar() {
  try {
    await navigator.clipboard.writeText(url.value)
    copiado.value = true
    setTimeout(() => { copiado.value = false }, 2000)
  } catch { /* sin portapapeles: no hacemos nada */ }
}
</script>
